import type { Appearance } from '@sunucupark/design-tokens/appearance';
import { applyAppearance } from '@sunucupark/ui';
import type { ColorScheme } from './theme';

/**
 * The personal layer of the look (ADR 0009 §8): the greys, the accent, the
 * font, the size and the corners this person chose, written onto `<html>` as
 * `--ds-*` properties over whatever the brand set.
 *
 * The tables behind those choices — every neutral family and every accent's
 * derived roles — are loaded only when someone has actually changed
 * something. A mailbox that looks the way it shipped pays nothing for the
 * ability to look otherwise.
 *
 * An inline style cannot be theme-conditional, so the declarations are those
 * of the scheme on screen and are written again when light turns into dark,
 * which is what {@link watchAppearance} keeps an eye on.
 */

/** What the product looks like when nobody has changed anything. */
export const DEFAULT_APPEARANCE: Appearance = {
  shell: 'modern',
  accent: 'brand',
  neutral: 'brand',
  radius: 'md',
  scale: 'md',
  font: 'system',
};

/** Whether this is still the look the product shipped with. */
export function isDefaultAppearance(appearance: Appearance): boolean {
  return (
    appearance.shell === DEFAULT_APPEARANCE.shell &&
    appearance.accent === DEFAULT_APPEARANCE.accent &&
    appearance.neutral === DEFAULT_APPEARANCE.neutral &&
    appearance.radius === DEFAULT_APPEARANCE.radius &&
    appearance.scale === DEFAULT_APPEARANCE.scale &&
    appearance.font === DEFAULT_APPEARANCE.font
  );
}

/** Which palette is actually on screen for a given choice. */
export function resolvedScheme(scheme: ColorScheme): 'light' | 'dark' {
  if (scheme !== 'system') return scheme;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Paints one appearance choice for the scheme currently on screen. */
export async function paintAppearance(appearance: Appearance, scheme: ColorScheme): Promise<void> {
  if (isDefaultAppearance(appearance)) {
    // Nothing of their own to paint — and the layer is cleared, so a reset takes effect too.
    applyAppearance({});
    return;
  }
  const { appearanceDeclarations } = await import('@sunucupark/design-tokens/appearance');
  applyAppearance(appearanceDeclarations(appearance, resolvedScheme(scheme)));
}

/**
 * Keeps the paint right while the system theme changes under a `system`
 * choice. Returns the function that stops watching.
 */
export function watchAppearance(read: () => { appearance: Appearance; colorScheme: ColorScheme }): () => void {
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const repaint = () => {
    const { appearance, colorScheme } = read();
    if (colorScheme === 'system') void paintAppearance(appearance, colorScheme);
  };
  media.addEventListener('change', repaint);
  return () => media.removeEventListener('change', repaint);
}
